import { useState } from "react";
import { Box, Collapse, Divider, LinearProgress, Link, Stack, Tooltip, Typography, alpha } from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  WarningAmberRounded as WarningIcon,
} from "@mui/icons-material";
import { assessPaidDateCoverage } from "../../utils/paidDateCoverage";
import { type DocumentTransformation, type TransformationTemplate } from "@/types";
import { SectionCard } from "@/components/common/SectionCard";
import type { AppliedSchemaMapping } from "../../hooks/useAppliedFieldMapping";
import { buildHeaderCoverage } from "./buildHeaderCoverage";
import { DetectedHeadersSummary } from "./DetectedHeadersSummary";
import { TransformationDetailsDialog } from "./TransformationDetailsDialog";
import { formatDocumentSubtype, formatExtractionMethod, formatProcessingTime } from "./transformationDisplay";

interface TransformationHeaderProps {
  /** Latest DocumentTransformation for the document. Source of extraction
   *  method, processing metadata, coverage stats. May be null when no
   *  transformation has run yet (e.g. storage-only or pre-processing). */
  transformation: DocumentTransformation | null;
  /** Transformation template that drove the mapping. May be null when the
   *  doc was processed without a template (i.e. automatic extraction). */
  template: TransformationTemplate | null | undefined;
  /** All schema mappings — used by the detected-headers coverage summary
   *  to compute mapped vs unmapped against the parser's column list. */
  schemaMappings: AppliedSchemaMapping[];
  /** Rows the latest load wrote as Paid with no paid date, from the load record
   *  (#7380, `claim_lines_no_paid_date`). Null when there is no load yet; then the
   *  header falls back to judging the file's mapping and coverage. */
  loadedWithoutPaidDate?: number | null;
}

/**
 * Top-of-tab metadata band for the Mapping tab.
 *
 * Header hierarchy is reviewer-first: template name (or "Automatic
 * extraction" when none) + sub-type + coverage + detected-headers summary
 * are surfaced at the top. The truly technical facts — extraction method,
 * page count, processing time — live behind a collapsed "Processing
 * details" toggle so common reviewers aren't asked to parse
 * `structured_parse` to use the page. Admins click once to see everything.
 *
 * Implementation note: `coverage_analysis.extraction_method_used` is not
 * populated by the backend in practice; the reliable source is
 * `processing_metadata.extraction_method`. The legacy Overview tab read
 * the former and rendered "Extraction Method: N/A" — that bug fixes as a
 * byproduct of sourcing from the right field here.
 *
 * Graceful degrades when no transformation or template exists.
 */
export const TransformationHeader = ({
  transformation,
  template,
  schemaMappings,
  loadedWithoutPaidDate = null,
}: TransformationHeaderProps) => {
  const meta = transformation?.processing_metadata;
  const coverage = transformation?.coverage_analysis;

  const templateName = template?.name;
  const templateVersion = template?.version;
  const hasTemplate = !!templateName;

  const subtype = formatDocumentSubtype(meta?.document_type);
  const extractionMethod = formatExtractionMethod(meta?.extraction_method);
  const pages = meta?.document_pages ?? null;
  const processingTime = formatProcessingTime(meta?.processing_time_ms ?? null);

  const coveragePercentage = transformation?.coverage_percentage ?? null;
  const totalExpected = coverage?.total_expected_fields ?? 0;
  const totalUnpopulated = coverage?.total_unpopulated_fields ?? 0;
  const populated = Math.max(0, totalExpected - totalUnpopulated);

  const headerCoverage = buildHeaderCoverage(meta?.detected_headers, schemaMappings);

  // #7380: a file that maps a paid amount but no paid date loads lines that never
  // count toward the deductible. The Mapping tab renders only once a document has
  // loaded (DocumentShow → DocumentStandardLayout), so this is the after-the-fact
  // explanation of the load's lines; the pre-Accept warning with the mapping action
  // is the "Before you approve" rail card (reviewChecks CHECK_PAID_DATE). The count
  // comes from the LOAD RECORD when there is one — rows written as Paid with no
  // date — because a reload over rows that already carry a date keeps those dates
  // and the file's own column count would overstate (expert review 2026-10-26).
  const paidDate = assessPaidDateCoverage({
    mappedFields: schemaMappings.flatMap((s) => s.mappings.map((m) => ({ schema: s.schema, field: m.targetField }))),
    coverage,
    lineCount: meta?.record_summary?.counts_by_schema?.claim_item ?? null,
  });
  const noDateCount =
    loadedWithoutPaidDate !== null
      ? loadedWithoutPaidDate
      : paidDate?.kind === "missing_paid_date"
        ? paidDate.count
        : 0;
  const paidDateWarning =
    paidDate?.kind === "missing_paid_amount"
      ? "No paid amount is mapped. Every line loaded as Submitted and counts nowhere toward the deductible."
      : noDateCount === null || (noDateCount ?? 0) > 0
        ? `${noDateCount !== null ? `${noDateCount.toLocaleString()} ${noDateCount === 1 ? "line was" : "lines were"} loaded` : "Lines were loaded"} without a paid date. ${noDateCount === 1 ? "It does" : "They do"} not count toward the deductible until a paid date is mapped and the document is reloaded, or the line's Paid Date is set by hand.`
        : null;
  const hasHeaderCoverage = headerCoverage.mapped.length + headerCoverage.unmapped.length > 0;

  // Whether there's anything to show behind the "Processing details" toggle.
  // If none of these facts are present, skip the toggle entirely.
  const hasProcessingDetails = !!extractionMethod || pages !== null || !!processingTime;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <SectionCard noPadding noDivider compact>
      <Box sx={{ p: 2, pb: 1.5 }}>
        {/* Row 1: Title + View transformation link.
            Template name is the title; sub-type sits beneath as supporting
            context. When no template ran, we use "Automatic extraction" as
            the title (replaces the prior "Ad-hoc extraction" jargon). */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ rowGap: 0.5, mb: hasHeaderCoverage || coveragePercentage !== null ? 2 : 0.5 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={600}>
                {hasTemplate ? templateName : "Automatic extraction"}
              </Typography>
              {hasTemplate && templateVersion && (
                <Typography variant="body2" color="text.secondary">
                  v{templateVersion}
                </Typography>
              )}
            </Stack>
            {subtype && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtype}
              </Typography>
            )}
          </Box>
          {transformation && (
            <Link
              component="button"
              type="button"
              onClick={() => setDialogOpen(true)}
              underline="hover"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <InfoIcon sx={{ fontSize: 16 }} />
              Transformation details
            </Link>
          )}
        </Stack>

        {/* Row 2: coverage progress (when available) */}
        {coveragePercentage !== null && (
          <Box sx={{ mb: hasHeaderCoverage ? 1.5 : 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}
              >
                Schema coverage
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {coveragePercentage.toFixed(1)}%
                {totalExpected > 0 && (
                  <Box component="span" sx={{ color: "text.secondary", fontWeight: 400, ml: 0.75 }}>
                    · {populated}/{totalExpected} fields populated
                  </Box>
                )}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, coveragePercentage))}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                "& .MuiLinearProgress-bar": { borderRadius: 1 },
              }}
            />
          </Box>
        )}

        {/* Row 2b: paid-date / paid-amount mapping warning (#7380) */}
        {paidDateWarning && (
          <Stack
            direction="row"
            alignItems="flex-start"
            columnGap={0.75}
            sx={{ mt: coveragePercentage !== null ? 1 : 0, mb: hasHeaderCoverage ? 1.5 : 0 }}
            data-testid="paid-date-mapping-warning"
          >
            <WarningIcon sx={{ fontSize: 18, color: "warning.main", mt: "1px" }} />
            <Typography variant="body2" sx={{ color: "warning.dark" }}>
              {paidDateWarning}
            </Typography>
          </Stack>
        )}

        {/* Row 3: detected-headers coverage summary (when available) */}
        {hasHeaderCoverage && (
          <>
            {coveragePercentage !== null && <Divider sx={{ my: 1.5 }} />}
            <DetectedHeadersSummary coverage={headerCoverage} />
          </>
        )}

        {/* Row 4: collapsed "Processing details" — admin / diagnostic info */}
        {hasProcessingDetails && (
          <Box sx={{ mt: hasHeaderCoverage || coveragePercentage !== null ? 1.5 : 0 }}>
            <Divider sx={{ mb: 1 }} />
            <Link
              component="button"
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              underline="hover"
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                color: "text.secondary",
              }}
            >
              {detailsOpen ? "Hide" : "Show"} processing details
              {detailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Link>
            <Collapse in={detailsOpen}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                divider={<DotDivider />}
                sx={{ rowGap: 0.5, mt: 1 }}
              >
                {extractionMethod && <Fact label="Extraction" value={extractionMethod} />}
                {pages !== null && <Fact label="Pages" value={String(pages)} />}
                {processingTime && (
                  <Fact
                    label="Processing time"
                    value={processingTime}
                    tooltip={
                      meta?.processing_time_ms !== undefined
                        ? `${meta.processing_time_ms.toLocaleString()} ms`
                        : undefined
                    }
                  />
                )}
              </Stack>
            </Collapse>
          </Box>
        )}
      </Box>
      <TransformationDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        transformation={transformation}
        template={template}
        schemaMappings={schemaMappings}
      />
    </SectionCard>
  );
};

interface FactProps {
  label: string;
  value: string;
  tooltip?: string;
}

const Fact = ({ label, value, tooltip }: FactProps) => {
  const content = (
    <Stack direction="row" alignItems="baseline" spacing={0.75}>
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography component="span" variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Stack>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      <Box>{content}</Box>
    </Tooltip>
  ) : (
    content
  );
};

const DotDivider = () => (
  <Box component="span" sx={{ color: "text.disabled", userSelect: "none" }} aria-hidden="true">
    ·
  </Box>
);
